import Time from '@/utils/Time';
import API from '@/services/api';
// import { Form } from '@CS/routes';
import { connect } from 'react-redux';
import FONTS from '@/assets/fonts.json';
// import { sentryDSN } from '@/utils/API';
import { User, MCD } from '@/services/app';
import { useState, useEffect } from 'react';
import { Login, Home, Game, IntroReady, GameResult, Onboarding } from '@/routes';

import { getLogged } from '@/redux/auth/selectors';
import type { UserState } from '@/redux/user/types';
import { RequestError } from '@CS/components/Popups';
import { getUserState } from '@/redux/user/selectors';
import { showConsole, isDEV } from '@/utils/Location';
import { useRecaptcha, useNavigateQuery } from '@/hooks';
import type { RequestErrorInfo } from '@/services/api/types';
import { Routes, Route, useLocation } from 'react-router-dom';
import { LogoABZ, MCDLogo, Background, IntroScreen } from '@/assets/images';
// import { withSentryReactRouterV6Routing } from '@sentry/react';
import TranslationProvider from '@CS/components/TranslationProvider';
import { useImages, useFonts, useLoader, useNetworkListener } from '@CS/hooks';

type ApplicationProps = {
  autoLogin?: boolean;
  logged?: boolean;
  login?: boolean;
  user: UserState;
};

const Application = ({
  user,
  logged,
  login = true,
  autoLogin = false
}: ApplicationProps) => {
  const imagesLoaded = useImages({
    Background,
    LogoABZ,
    MCDLogo,
    IntroScreen
  });

  const { search } = useLocation();
  const navigate = useNavigateQuery();
  const fontsLoaded = useFonts(FONTS);

  const network = useNetworkListener();
  const { recaptcha } = useRecaptcha();
  const onLoad = () => setLoading(false);

  const [loading, setLoading] = useState(true);
  const cancelLogin = () => setLoginError(void 0);

  const loader = useLoader(
    loading || !recaptcha || !imagesLoaded || !fontsLoaded
  );

  const { email, points, country, readonlyData } = user ?? {};
  const [loginError, setLoginError] = useState<RequestErrorInfo>();

  const toggleFullscreen = () =>
    !isDEV() && ~MCD.toggleFullscreen() && MCD.orientationScreen();

  const onVisibilityChange = () =>
    document.visibilityState === 'visible' && toggleFullscreen();

  const getUserData = () =>
    Promise.all([User.checkUserPoints(), User.checkUserData()]);

  const handleAuth = () =>
    (!email || typeof points !== 'number') &&
    getUserData().then(onLoad).catch(loginRedirect);

  const initApplication = () => {
    API.revokeToken();
    toggleFullscreen();
    User.checkUserCountry();
    User.checkUserLanguage();
    login ? handleAuth() : onLoad();
  };

  const loginRedirect = () => {
    setLoading(false);

    !autoLogin
      ? navigate('/login')
      : MCD.promptLogin()
          .then(onLogin)
          .catch(error => {
            error = typeof error === 'string' && { message: error };
            setLoginError({ message: 'Prompt login error.', ...error });
          });
  };

  const onLogin = async () => {
    toggleFullscreen();
    await getUserData();
    setLoading(false);
    navigate('/');
  };

  useEffect(() => {
    const userInfo = country && email;
    const ready = recaptcha && !logged;

    ready &&
      userInfo &&
      API.login(user).catch(error =>
        setLoginError(API.getRequestErrorInfo(error))
      );
  }, [recaptcha, country, logged, email, user]);

  useEffect(() => {
    showConsole((readonlyData as { console?: boolean })?.console);
  }, [readonlyData]);

  useEffect(() => {
    MCD.App.mcd && !loading && initApplication();
  }, [MCD.App.mcd]);

  useEffect(() => {
    Time.setCurrentTime(search);
  }, [search]);

  useEffect(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('mcdBridgeReady', initApplication);
    (MCD.App.mcd || isDEV()) && initApplication();

    return () => {
      document.removeEventListener('mcdBridgeReady', initApplication);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  /**
   * Sentry Tracing Routes Wrapper:
   *
  const RoutesWrapper = sentryDSN
    ? withSentryReactRouterV6Routing(Routes)
    : Routes; */

  return (
    loader ||
    network || (
      <TranslationProvider lang={user.language}>
        <Routes>
          {/* <RoutesWrapper> */}
          {!autoLogin && (
            <Route path="/login" element={<Login onLogin={onLogin} />} />
          )}
          <Route path="/" element={<Home />} />
          <Route path="/onboarding" element={<Onboarding />} />
          <Route path="/intro-ready" element={<IntroReady />} />
          <Route path="/game" element={<Game />} />
          <Route path="/game-result" element={<GameResult />} />
          {/* <Route path="/form" element={<Form user={user} />} /> */}
          {/* </RoutesWrapper> */}
        </Routes>

        {loginError && <RequestError info={loginError} onClose={cancelLogin} />}
      </TranslationProvider>
    )
  );
};

const mapStateToProps = (state: never) => ({
  user: getUserState(state),
  logged: getLogged(state)
});

export default connect(mapStateToProps)(Application);
